import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  TrendingUp,
  Shield,
  Users,
  BookOpen,
  ArrowRight,
  CheckCircle,
  Bitcoin,
  LineChart,
  Wallet,
  AlertTriangle,
} from "lucide-react";

const offerings = [
  {
    icon: Bitcoin,
    title: "Crypto Advisory",
    description: "BTC & selected altcoin guidance with clear risk levels.",
    color: "primary",
  },
  {
    icon: LineChart,
    title: "Stock & Equity Guidance",
    description: "Long-term wealth-building strategies.",
    color: "secondary",
  },
  {
    icon: Wallet,
    title: "Passive Income Framework",
    description: "Low-stress, structured investing — not day trading.",
    color: "accent",
  },
];

const whyUs = [
  "No upfront advisory fees",
  "Profit-aligned model",
  "Years of experience",
  "Community-driven approach",
  "Education + Execution",
];

const howItWorks = [
  { step: "01", title: "Join PassivePay", description: "Create your account and connect with our community" },
  { step: "02", title: "Receive Signals", description: "Get guided buy & sell signals based on market analysis" },
  { step: "03", title: "Grow Capital", description: "Capital grows with disciplined risk management" },
];

export default function Home() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Generating Passive Income for Everyone Globally</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6 leading-tight">
              Build Passive Income{" "}
              <span className="text-primary neon-text">Without Trading</span>{" "}
              Full-Time
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl">
              Guided investing in crypto, stocks & equity with a profit-only commission model.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="neon-glow font-heading font-bold uppercase tracking-wider px-8" data-testid="hero-cta">
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white/20 hover:bg-white/5">
                  Learn More
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is PassivePay */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
              What is <span className="text-primary">PassivePay</span>?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              PassivePay is a guided investment ecosystem designed to help individuals grow wealth steadily 
              without constant market monitoring. We combine years of experience with disciplined risk management 
              to deliver sustainable returns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              How It <span className="text-primary">Works</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="glass-card rounded-2xl p-8 h-full hover:border-primary/30 transition-colors">
                  <span className="text-6xl font-heading font-black text-primary/20 absolute top-4 right-4">
                    {item.step}
                  </span>
                  <h3 className="text-xl font-heading font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              What We <span className="text-primary">Offer</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {offerings.map((offer, i) => {
              const Icon = offer.icon;
              return (
                <motion.div
                  key={offer.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-2xl p-8 hover:border-primary/30 transition-all group"
                >
                  <div className={`w-14 h-14 rounded-xl bg-${offer.color}/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 text-${offer.color}`} />
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-3">{offer.title}</h3>
                  <p className="text-muted-foreground">{offer.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why PassivePay */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-8">
                Why <span className="text-primary">PassivePay</span>?
              </h2>
              <div className="space-y-4">
                {whyUs.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <Shield className="w-10 h-10 text-primary" />
                <div>
                  <h3 className="font-heading font-bold text-xl">Trust & Transparency</h3>
                  <p className="text-muted-foreground">Your financial success is our priority</p>
                </div>
              </div>
              <div className="space-y-4">
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <BookOpen className="w-5 h-5 text-primary" />
                  <span>Education-first approach</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                  <Users className="w-5 h-5 text-secondary" />
                  <span>Community-driven growth</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/10 border border-accent/20">
                  <TrendingUp className="w-5 h-5 text-accent" />
                  <span>Profit-aligned incentives</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-xl p-6 border-yellow-500/20"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-heading font-bold mb-2">Disclaimer</h4>
                <p className="text-sm text-muted-foreground">
                  Investing involves risk. Past performance is not indicative of future results. 
                  No guaranteed returns. Always do your own research before making investment decisions.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_31e6f2da-e30e-4604-a2ae-ae4870f67bdf/artifacts/m4cp44gx_passivepay_new_logo.png" 
                alt="PassivePay" 
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="font-heading font-bold">PassivePay</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/paycoin" className="hover:text-primary transition-colors">PayCoin</Link>
              <Link to="/crypto-loans" className="hover:text-primary transition-colors">Crypto Loans</Link>
              <Link to="/about" className="hover:text-primary transition-colors">About</Link>
              <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 PassivePay. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
